import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { COUNTRIES } from "../lib/countries";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { MailCheck, AlertCircle, Info } from "lucide-react";

export default function RegisterPage() {
    const { register } = useAuth();
    const navigate = useNavigate();
    const [form, setForm] = useState({
        name: "", email: "", password: "", countryName: "United States", dial: "+1", phone: "",
    });
    const [consent, setConsent] = useState({ terms: false, transactional: false, marketing: false });
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [registered, setRegistered] = useState(null); // {email, dev_verify_url}

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const onCountry = (name) => {
        const c = COUNTRIES.find((x) => x.name === name);
        setForm((f) => ({ ...f, countryName: name, dial: c ? c.dial : f.dial }));
    };

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (!consent.terms) {
            setError("You must agree to the Terms of Use and Privacy Policy to continue.");
            return;
        }
        setLoading(true);
        try {
            const data = await register({
                name: form.name,
                email: form.email,
                password: form.password,
                country: form.countryName,
                dial_code: form.dial,
                phone: form.phone,
                consent_terms: consent.terms,
                consent_transactional: consent.transactional,
                consent_marketing: consent.marketing,
            });
            setRegistered({ email: form.email, dev_verify_url: data.dev_verify_url });
        } catch (err) {
            setError(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    if (registered) {
        return (
            <div className="max-w-md mx-auto px-4 sm:px-6 py-16" data-testid="register-confirm">
                <div className="panel p-9 text-center">
                    <span className="grid place-items-center w-16 h-16 mx-auto rounded-full bg-accent-soft text-accent">
                        <MailCheck size={30} />
                    </span>
                    <h1 className="font-display text-2xl font-bold tracking-[-0.015em] mt-5">Confirm your email</h1>
                    <p className="mt-3 text-sm text-dim leading-relaxed">
                        We've sent a confirmation link to <span className="font-medium text-foreground">{registered.email}</span>.
                        Click it to activate your account and join the mailing list.
                    </p>
                    {registered.dev_verify_url && (
                        <div className="mt-6 rounded-xl border border-line bg-surface-sunken p-4 text-left">
                            <p className="eyebrow text-dim mb-2">Demo mode — no email is sent</p>
                            <Link
                                to={registered.dev_verify_url.replace(/^https?:\/\/[^/]+/, "")}
                                data-testid="dev-verify-link"
                                className="text-sm text-accent link-underline break-all"
                            >
                                Confirm your email now →
                            </Link>
                        </div>
                    )}
                    <button
                        onClick={() => navigate("/")}
                        data-testid="register-confirm-home"
                        className="mt-7 w-full rounded-full bg-accent text-accent-foreground py-3.5 text-sm font-semibold hover:shadow-glow hover:-translate-y-0.5 transition-[transform,box-shadow] duration-300"
                    >
                        Continue to the site
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-lg mx-auto px-4 sm:px-6 py-14" data-testid="register-page">
            <div className="panel p-8 sm:p-9">
                <span className="eyebrow inline-flex rounded-full bg-accent-soft px-3.5 py-1.5 text-accent">Free membership</span>
                <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-[-0.02em] mt-2.5">
                    Join the mailing list
                </h1>
                <p className="mt-2.5 text-sm text-dim leading-relaxed">
                    Get market intelligence and personal-finance briefings by email and SMS.
                </p>

                <form onSubmit={submit} className="mt-8 space-y-5">
                    <div>
                        <label className="eyebrow text-dim block mb-2">Full name</label>
                        <input required value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="register-name" className="field" />
                    </div>
                    <div>
                        <label className="eyebrow text-dim block mb-2">Email</label>
                        <input type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="register-email" className="field" />
                    </div>
                    <div>
                        <label className="eyebrow text-dim block mb-2">Password</label>
                        <input type="password" required minLength={6} value={form.password} onChange={(e) => set("password", e.target.value)} data-testid="register-password" className="field" />
                    </div>
                    <div>
                        <label className="eyebrow text-dim block mb-2">Country</label>
                        <Select value={form.countryName} onValueChange={onCountry}>
                            <SelectTrigger
                                data-testid="register-country"
                                className="field h-auto shadow-none data-[placeholder]:text-dim"
                            >
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="rounded-xl border border-line bg-popover max-h-72 shadow-lift">
                                {COUNTRIES.map((c) => (
                                    <SelectItem key={c.code} value={c.name} className="rounded-lg font-sans text-sm">
                                        {c.name} <span className="font-mono text-xs text-dim">({c.dial})</span>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <label className="eyebrow text-dim block mb-2">Phone number</label>
                        <div className="flex gap-2">
                            <span
                                className="inline-flex items-center rounded border border-line bg-surface-raised px-3.5 font-mono text-sm text-dim shrink-0"
                                data-testid="register-dial"
                            >
                                {form.dial}
                            </span>
                            <input
                                type="tel"
                                required
                                value={form.phone}
                                onChange={(e) => set("phone", e.target.value)}
                                placeholder="555 123 4567"
                                data-testid="register-phone"
                                className="field font-mono"
                            />
                        </div>
                    </div>

                    {/* Consents */}
                    <div className="space-y-4 rounded-xl border border-line bg-surface-sunken p-4">
                        <label className="flex items-start gap-3 cursor-pointer" data-testid="consent-terms-label">
                            <Checkbox
                                checked={consent.terms}
                                onCheckedChange={(v) => setConsent((c) => ({ ...c, terms: !!v }))}
                                data-testid="consent-terms"
                                className="rounded-sm border-line-strong mt-0.5 shadow-none"
                            />
                            <span className="text-sm leading-snug text-dim">
                                I agree to the <Link to="/terms" className="text-accent link-underline">Terms of Use</Link> and{" "}
                                <Link to="/privacy" className="text-accent link-underline">Privacy Policy</Link>.{" "}
                                <span className="text-neg-ink">*</span>
                            </span>
                        </label>
                        <label className="flex items-start gap-3 cursor-pointer" data-testid="consent-transactional-label">
                            <Checkbox
                                checked={consent.transactional}
                                onCheckedChange={(v) => setConsent((c) => ({ ...c, transactional: !!v }))}
                                data-testid="consent-transactional"
                                className="rounded-sm border-line-strong mt-0.5 shadow-none"
                            />
                            <span className="text-sm leading-snug text-dim">
                                I consent to receive transactional messages (account and service notifications) by email and SMS.
                            </span>
                        </label>
                        <label className="flex items-start gap-3 cursor-pointer" data-testid="consent-marketing-label">
                            <Checkbox
                                checked={consent.marketing}
                                onCheckedChange={(v) => setConsent((c) => ({ ...c, marketing: !!v }))}
                                data-testid="consent-marketing"
                                className="rounded-sm border-line-strong mt-0.5 shadow-none"
                            />
                            <span className="text-sm leading-snug text-dim">
                                I consent to receive marketing messages (newsletters and offers) by email and SMS.
                            </span>
                        </label>
                    </div>

                    {/* SMS legal disclosure */}
                    <p
                        className="flex items-start gap-2.5 rounded-xl border border-line bg-surface-sunken p-3.5 text-[11px] text-dim leading-relaxed"
                        data-testid="sms-disclosure"
                    >
                        <Info size={14} className="shrink-0 mt-0.5 text-dim" />
                        <span>
                            By providing your phone number and checking the boxes above, you agree to receive text messages.
                            Consent is not a condition of purchase. Message and data rates may apply. Message frequency varies,
                            up to 4 messages per week. Reply STOP to opt out at any time, or HELP for help.
                        </span>
                    </p>

                    {error && (
                        <p
                            className="flex items-start gap-2.5 rounded-xl border border-neg/30 bg-neg/[0.07] p-3 text-sm text-neg-ink"
                            data-testid="register-error"
                        >
                            <AlertCircle size={16} className="shrink-0 mt-0.5" />
                            {error}
                        </p>
                    )}

                    <button
                        type="submit"
                        disabled={loading}
                        data-testid="register-submit"
                        className="w-full rounded-full bg-accent text-accent-foreground py-3.5 text-sm font-semibold hover:shadow-glow hover:-translate-y-0.5 transition-[transform,box-shadow] duration-300 disabled:opacity-50 disabled:hover:shadow-none disabled:hover:translate-y-0"
                    >
                        {loading ? "Creating account…" : "Create account"}
                    </button>
                </form>

                <p className="mt-7 pt-6 border-t border-line text-sm text-dim">
                    Already a member? <Link to="/login" className="text-accent link-underline font-medium">Log in</Link>
                </p>
            </div>
        </div>
    );
}
